import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Navigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Package, Wrench, ShoppingBag, TrendingUp, Trash2, Edit3, ShieldCheck, AlertTriangle, Wallet, Clock, CheckCircle2, Store, Upload, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const emptyProduct = { title: "", description: "", price: 0, prices: { EUR: 0, USD: 0, CDF: 0 }, stock: 1, category_id: "", images: [], location: "", available: true, delivery_options: ["pickup", "local_delivery"], payment_methods: ["card"] };
const emptyService = { title: "", description: "", price: 0, prices: { EUR: 0, USD: 0, CDF: 0 }, category_id: "", images: [], zone: "", available: true, payment_methods: ["card"], booking_enabled: false, slot_duration_min: 60, available_slots: [] };

function ItemForm({ kind, item, categories, onSave, onCancel, t }) {
  const { tCategory } = useLang();
  const initial = item ? { ...(kind === "product" ? emptyProduct : emptyService), ...item, prices: { EUR: 0, USD: 0, CDF: 0, ...(item.prices || {}) } } : (kind === "product" ? emptyProduct : emptyService);
  const [f, setF] = useState(initial);
  const [showEn, setShowEn] = useState(!!(initial.title_en || initial.description_en));
  const [translating, setTranslating] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setPrice = (cur, v) => setF((x) => ({ ...x, prices: { ...x.prices, [cur]: parseFloat(v) || 0 } }));

  const autoTranslate = async () => {
    if (!f.title || !f.description) { toast.error(t("if.translate_fill_first")); return; }
    setTranslating(true);
    try {
      // If the item is already saved (has id) hit the stateless translate endpoint on its record.
      // Else we do a one-shot translation via a lightweight helper — reuse the item endpoint by
      // creating a temporary sandbox: for a NEW item we simply call the endpoint with a dummy id? No.
      // Simplest: post to the item's ID if exists, otherwise call a generic translator endpoint.
      // We add a generic endpoint below for the "not yet saved" case.
      const path = item && item.id
        ? `/${kind === "product" ? "products" : "services"}/${item.id}/translate?target=en`
        : `/translate?target=en`;
      const { data } = await api.post(path, item && item.id ? undefined : { title: f.title, description: f.description });
      set("title_en", data.title_en || data.title || "");
      set("description_en", data.description_en || data.description || "");
      toast.success(t("if.translated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("if.error"));
    } finally { setTranslating(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      set("images", [...(f.images || []), `${backendUrl}${data.url}`]);
      toast.success(t("if.image_added"));
    } catch { toast.error(t("if.upload_fail")); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, price: f.prices?.EUR || f.price || 0 };
      const endpoint = kind === "product" ? "/products" : "/services";
      if (item && item.id) await api.put(`${endpoint}/${item.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(item?.id ? t("if.updated") : t("if.created"));
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || t("if.error")); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${kind}-form`}>
      <div className="space-y-2"><Label>{t("if.title")}</Label><Input required value={f.title} onChange={(e) => set("title", e.target.value)} data-testid={`${kind}-title`}/></div>
      <div className="space-y-2"><Label>{t("if.description")}</Label><Textarea required rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid={`${kind}-description`}/></div>

      {/* EN version — collapsible */}
      <div className="border border-dashed border-[#1A3322]/20 rounded-lg p-3 bg-[#F5F5F0]/40">
        <button type="button" onClick={() => setShowEn((v) => !v)} className="text-sm font-semibold text-[#1A3322] hover:underline flex items-center gap-2" data-testid={`${kind}-toggle-en`}>
          🇬🇧 {showEn ? t("if.en_toggle_hide") : t("if.en_toggle_show")}
        </button>
        {showEn && (
          <div className="mt-3 space-y-3" data-testid={`${kind}-en-panel`}>
            <p className="text-xs text-[#4B5563]">{t("if.en_hint")}</p>
            <div className="space-y-2"><Label>{t("if.title_en")}</Label><Input value={f.title_en || ""} onChange={(e) => set("title_en", e.target.value)} data-testid={`${kind}-title-en`}/></div>
            <div className="space-y-2"><Label>{t("if.description_en")}</Label><Textarea rows={4} value={f.description_en || ""} onChange={(e) => set("description_en", e.target.value)} data-testid={`${kind}-description-en`}/></div>
            <Button type="button" size="sm" variant="outline" onClick={autoTranslate} disabled={translating} className="rounded-full" data-testid={`${kind}-auto-translate`}>
              {translating ? t("if.translating") : t("if.auto_translate")}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t("if.prices")}</Label>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="text-xs text-[#4B5563] mb-1">🇫🇷 EUR</p>
            <Input type="number" step="0.01" required value={f.prices.EUR} onChange={(e) => setPrice("EUR", e.target.value)} data-testid={`${kind}-price-eur`}/>
          </div>
          <div>
            <p className="text-xs text-[#4B5563] mb-1">🌍 USD</p>
            <Input type="number" step="0.01" value={f.prices.USD} onChange={(e) => setPrice("USD", e.target.value)} data-testid={`${kind}-price-usd`}/>
          </div>
          <div>
            <p className="text-xs text-[#4B5563] mb-1">🇨🇩 CDF</p>
            <Input type="number" step="1" value={f.prices.CDF} onChange={(e) => setPrice("CDF", e.target.value)} data-testid={`${kind}-price-cdf`}/>
          </div>
        </div>
        <p className="text-[11px] text-[#4B5563]">{t("if.price_note")}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {kind === "product" && <div className="space-y-2"><Label>{t("if.stock")}</Label><Input type="number" value={f.stock} onChange={(e) => set("stock", parseInt(e.target.value || "0"))} data-testid={`${kind}-stock`}/></div>}
      </div>
      <div className="space-y-2">
        <Label>{t("if.category")}</Label>
        <Select value={f.category_id} onValueChange={(v) => set("category_id", v)}>
          <SelectTrigger data-testid={`${kind}-category`}><SelectValue placeholder={t("if.choose")}/></SelectTrigger>
          <SelectContent>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{tCategory(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label>{kind === "service" ? t("if.zone") : t("if.location")}</Label><Input value={kind==="service"?f.zone:f.location} onChange={(e) => set(kind==="service"?"zone":"location", e.target.value)} data-testid={`${kind}-location`}/></div>

      {kind === "service" && (
        <div className="border border-dashed border-[#1A3322]/20 rounded-lg p-3 bg-[#F5F5F0]/40 space-y-3" data-testid="booking-panel">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!f.booking_enabled}
              onChange={(e) => set("booking_enabled", e.target.checked)}
              className="w-4 h-4 accent-[#1A3322]"
              data-testid="booking-enabled-toggle"
            />
            <span className="text-sm font-semibold text-[#1A3322]">📅 {t("if.booking_enable")}</span>
          </label>
          {f.booking_enabled && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t("if.booking_duration")}</Label>
                  <Select value={String(f.slot_duration_min || 60)} onValueChange={(v) => set("slot_duration_min", parseInt(v))}>
                    <SelectTrigger data-testid="booking-duration"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60, 90, 120, 180, 240].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("if.booking_add_slot")}</Label>
                <div className="flex gap-2">
                  <Input
                    type="datetime-local"
                    id={`slot-picker-${kind}`}
                    data-testid="booking-slot-picker"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const el = document.getElementById(`slot-picker-${kind}`);
                      if (!el || !el.value) { toast.error(t("if.booking_pick_first")); return; }
                      // Convert to ISO
                      const iso = new Date(el.value).toISOString();
                      if ((f.available_slots || []).includes(iso)) { toast.error(t("if.booking_dup")); return; }
                      set("available_slots", [...(f.available_slots || []), iso].sort());
                      el.value = "";
                    }}
                    className="rounded-full"
                    data-testid="booking-add-slot-btn"
                  >
                    <Plus className="w-4 h-4 mr-1"/>{t("if.booking_add")}
                  </Button>
                </div>
              </div>
              {(f.available_slots || []).length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="booking-slots-list">
                  <p className="text-[11px] text-[#4B5563]">{f.available_slots.length} {t("if.booking_slots_count")}</p>
                  {f.available_slots.map((s) => (
                    <div key={s} className="flex items-center justify-between bg-white border border-[#1A3322]/10 rounded px-2.5 py-1.5 text-xs">
                      <span className="font-mono">{new Date(s).toLocaleString()}</span>
                      <button
                        type="button"
                        onClick={() => set("available_slots", f.available_slots.filter((x) => x !== s))}
                        className="text-[#C85A32] hover:text-[#D98A2C]"
                        data-testid={`booking-remove-slot-${s}`}
                      ><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[#4B5563]">{t("if.booking_hint")}</p>
            </>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label>{t("if.images")}</Label>
        <Input type="file" accept="image/*" onChange={uploadImage} data-testid={`${kind}-image-upload`}/>
        <div className="flex flex-wrap gap-2 mt-2">
          {(f.images || []).map((img) => (
            <div key={img} className="relative">
              <img src={img} alt="" className="w-16 h-16 object-cover rounded"/>
              <button type="button" onClick={() => set("images", f.images.filter((x) => x !== img))} className="absolute -top-2 -right-2 bg-[#C85A32] text-white rounded-full w-5 h-5 text-xs">×</button>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#4B5563]">{t("if.image_url")}</p>
        <Input placeholder="https://…" onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); if (e.target.value) { set("images", [...(f.images||[]), e.target.value]); e.target.value = ""; } }
        }} data-testid={`${kind}-image-url`}/>
      </div>
      <div className="flex gap-2">
        <Button type="submit" className="pill-btn bg-[#1A3322] rounded-full" data-testid={`${kind}-save`}>{t("if.save")}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>{t("if.cancel")}</Button>
      </div>
    </form>
  );
}

export default function SellerDashboard() {
  const { user, loading } = useAuth();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKind, setDialogKind] = useState("product");
  const [kyc, setKyc] = useState({ status: "not_started", rejection_reason: "" });
  const [revenue, setRevenue] = useState({ held_eur: 0, releasable_eur: 0, paid_out_eur: 0, lines: [], commission_rate: 0.1, retention_days: 5 });
  const [shop, setShop] = useState({ name: "", description: "", logo_url: "", cover_image: "", location: "", published: true });
  const [savingShop, setSavingShop] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvKind, setCsvKind] = useState("product");
  const [csvFile, setCsvFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    const [s, p, sv, o, c, k, r, shopR] = await Promise.all([
      api.get("/seller/stats"),
      api.get(`/products?seller_id=${user.id}&limit=200`),
      api.get(`/services?provider_id=${user.id}&limit=200`),
      api.get("/orders/received"),
      api.get("/categories"),
      api.get("/kyc/me"),
      api.get("/seller/revenue"),
      api.get("/shops/mine"),
    ]);
    setStats(s.data); setProducts(p.data); setServices(sv.data); setOrders(o.data); setCategories(c.data);
    setKyc(k.data); setRevenue(r.data);
    if (shopR.data) setShop({
      name: shopR.data.name || "",
      description: shopR.data.description || "",
      logo_url: shopR.data.logo_url || "",
      cover_image: shopR.data.cover_image || "",
      location: shopR.data.location || "",
      published: shopR.data.published !== false,
    });
  }, [user?.id]);
  useEffect(() => { if (user) load().catch(() => {}); }, [user, load]);

  if (loading) return null;
  if (!user) return <Navigate to="/connexion" />;
  if (user.role !== "seller" && user.role !== "admin") return <Navigate to="/" />;

  const openNew = (k) => { setDialogKind(k); setEditing(null); setDialogOpen(true); };
  const openEdit = (k, item) => { setDialogKind(k); setEditing(item); setDialogOpen(true); };

  const toggleFeatured = async (kind, id, current) => {
    try {
      const r = await api.patch(`/admin/${kind === "product" ? "products" : "services"}/${id}/featured`);
      toast.success(r.data.featured_as_new ? "✨ Épinglé en Nouveautés" : "Retiré des Nouveautés");
      const setter = kind === "product" ? setProducts : setServices;
      setter((list) => list.map((it) => (it.id === id ? { ...it, featured_as_new: r.data.featured_as_new } : it)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (kind, id) => {
    if (!confirm(t("sd.confirm_delete"))) return;
    await api.delete(`/${kind === "product" ? "products" : "services"}/${id}`);
    toast.success(t("sd.deleted"));
    load();
  };

  const updateOrderStatus = async (id, status) => {
    await api.post(`/orders/${id}/status?status=${status}`);
    toast.success(t("sd.status_updated"));
    load();
  };

  const uploadShopImage = async (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setShop((prev) => ({ ...prev, [field]: `${backendUrl}${data.url}` }));
      toast.success(t("if.image_added"));
    } catch { toast.error(t("if.upload_fail")); }
  };

  const saveShop = async () => {
    setSavingShop(true);
    try {
      await api.put("/shops/mine", shop);
      toast.success(t("sd.shop_saved"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
    finally { setSavingShop(false); }
  };

  const openCsv = (kind) => {
    setCsvKind(kind); setCsvFile(null); setCsvResult(null); setCsvOpen(true);
  };

  const submitCsv = async () => {
    if (!csvFile) { toast.error(t("sd.csv_pick_file")); return; }
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const endpoint = csvKind === "product" ? "/products/import-csv" : "/services/import-csv";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(data);
      if (data.created > 0) toast.success(`${data.created} ${t("sd.csv_imported")}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setCsvBusy(false); }
  };

  const confirmTransfer = async (id) => {
    if (!confirm(t("sd.confirm_transfer_prompt"))) return;
    try {
      await api.post(`/orders/${id}/confirm-transfer`);
      toast.success(t("sd.transfer_confirmed"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
  };

  const locale = lang === "en" ? "en-GB" : "fr-FR";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12" data-testid="seller-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="heading font-extrabold text-3xl sm:text-4xl">{t("sd.title")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/vendeur/abonnement">
            <Button size="sm" variant="outline" className="rounded-full" data-testid="subscription-link">
              💎 {t("sd.subscription_link")}
            </Button>
          </Link>
        {kyc.status === "verified" && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-green-100 text-green-800" data-testid="kyc-badge-verified">
            <ShieldCheck className="w-3.5 h-3.5"/> {t("sd.verified")}
          </span>
        )}
        {kyc.status === "pending" && (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-amber-100 text-amber-900" data-testid="kyc-badge-pending">
            <Clock className="w-3.5 h-3.5"/> {t("sd.pending_verif")}
          </span>
        )}
        {(kyc.status === "not_started" || kyc.status === "rejected") && (
          <Link to="/vendeur/verification"><Button size="sm" className="pill-btn rounded-full bg-[#C85A32] hover:bg-[#D98A2C]" data-testid="kyc-cta">
            <ShieldCheck className="w-4 h-4 mr-2"/>{kyc.status === "rejected" ? t("sd.correct_verif") : t("sd.verify_cta")}
          </Button></Link>
        )}
        </div>
      </div>

      {(kyc.status === "not_started" || kyc.status === "rejected") && (
        <div className="mb-6 rounded-lg border-2 border-[#C85A32]/30 bg-[#FFF7ED] p-4 flex items-start gap-3" data-testid="kyc-banner">
          <AlertTriangle className="w-5 h-5 text-[#C85A32] shrink-0 mt-0.5"/>
          <div className="flex-1 text-sm">
            <p className="font-semibold text-[#111812]">
              {kyc.status === "rejected" ? t("sd.verif_refused") : t("sd.verif_required")}
            </p>
            <p className="text-[#4B5563] mt-1">
              {kyc.status === "rejected" && kyc.rejection_reason ? (
                <>{t("sd.verif_reason")} <em>{kyc.rejection_reason}</em>{t("sd.verif_correct_hint")}</>
              ) : (
                <>{t("sd.verif_help_pre")} {kyc.unverified_max || 3} {t("sd.verif_help_post")}</>
              )}
            </p>
          </div>
          <Link to="/vendeur/verification">
            <Button size="sm" className="pill-btn rounded-full bg-[#1A3322] hover:bg-[#24472F] shrink-0" data-testid="kyc-banner-cta">{t("sd.start")}</Button>
          </Link>
        </div>
      )}
      {kyc.status === "suspended" && (
        <div className="mb-6 rounded-lg border-2 border-red-300 bg-red-50 p-4 text-sm text-red-900" data-testid="kyc-suspended">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> {t("sd.suspended")}</p>
          <p className="mt-1">{kyc.rejection_reason || t("sd.suspended_contact")}</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[
          { icon: Package, label: t("sd.stat_products"), value: stats?.products || 0 },
          { icon: Wrench, label: t("sd.stat_services"), value: stats?.services || 0 },
          { icon: ShoppingBag, label: t("sd.stat_orders"), value: stats?.orders || 0 },
          { icon: TrendingUp, label: t("sd.stat_revenue"), value: `${(stats?.revenue || 0).toFixed(2)} €` },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-[#1A3322]/10 rounded-lg p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
            <s.icon className="w-6 h-6 text-[#C85A32] mb-2"/>
            <p className="text-xs uppercase tracking-wider text-[#4B5563]">{s.label}</p>
            <p className="heading font-extrabold text-2xl mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="products" data-testid="tab-products">{t("sd.tab_products")}</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">{t("sd.tab_services")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">{t("sd.tab_orders")}</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">{t("sd.tab_revenue")}</TabsTrigger>
          <TabsTrigger value="shop" data-testid="tab-shop">{t("sd.tab_shop")}</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <p className="label-uppercase text-xs">{products.length} {t("sd.count_products")}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => openCsv("product")} className="rounded-full" data-testid="csv-import-product-btn"><Upload className="w-4 h-4 mr-2"/>{t("sd.import_csv")}</Button>
              <Button onClick={() => openNew("product")} className="pill-btn bg-[#1A3322] rounded-full" data-testid="new-product-btn"><Plus className="w-4 h-4 mr-2"/>{t("sd.new_product")}</Button>
            </div>
          </div>
          <div className="space-y-2" data-testid="products-list">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-white border border-[#1A3322]/10 rounded-lg p-3">
                <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover rounded"/>
                <div className="flex-1">
                  <p className="font-semibold flex items-center gap-2">
                    {p.title}
                    {p.featured_as_new && <span className="inline-flex items-center gap-1 text-[10px] bg-[#C85A32] text-white px-1.5 py-0.5 rounded-full font-bold uppercase" title="Épinglé en Nouveautés"><Sparkles className="w-2.5 h-2.5"/>Nouveau</span>}
                  </p>
                  <p className="text-xs text-[#4B5563]">{t("sd.stock")} {p.stock} · {p.price.toFixed(2)} €</p>
                </div>
                {user?.role === "admin" && (
                  <Button size="icon" variant="outline" onClick={() => toggleFeatured("product", p.id, p.featured_as_new)} title={p.featured_as_new ? "Retirer des Nouveautés" : "Épingler en Nouveautés"} data-testid={`toggle-featured-product-${p.id}`} className={p.featured_as_new ? "bg-[#FFF7ED] border-[#C85A32]" : ""}>
                    <Sparkles className={`w-4 h-4 ${p.featured_as_new ? "text-[#C85A32]" : ""}`}/>
                  </Button>
                )}
                <Button size="icon" variant="outline" onClick={() => openEdit("product", p)}><Edit3 className="w-4 h-4"/></Button>
                <Button size="icon" variant="ghost" onClick={() => del("product", p.id)}><Trash2 className="w-4 h-4 text-[#C85A32]"/></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <p className="label-uppercase text-xs">{services.length} {t("sd.count_services")}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => openCsv("service")} className="rounded-full" data-testid="csv-import-service-btn"><Upload className="w-4 h-4 mr-2"/>{t("sd.import_csv")}</Button>
              <Button onClick={() => openNew("service")} className="pill-btn bg-[#1A3322] rounded-full" data-testid="new-service-btn"><Plus className="w-4 h-4 mr-2"/>{t("sd.new_service")}</Button>
            </div>
          </div>
          <div className="space-y-2" data-testid="services-list">
            {services.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-white border border-[#1A3322]/10 rounded-lg p-3">
                <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover rounded"/>
                <div className="flex-1">
                  <p className="font-semibold flex items-center gap-2">
                    {p.title}
                    {p.featured_as_new && <span className="inline-flex items-center gap-1 text-[10px] bg-[#C85A32] text-white px-1.5 py-0.5 rounded-full font-bold uppercase"><Sparkles className="w-2.5 h-2.5"/>Nouveau</span>}
                  </p>
                  <p className="text-xs text-[#4B5563]">{p.price.toFixed(2)} € · {p.zone}</p>
                </div>
                {user?.role === "admin" && (
                  <Button size="icon" variant="outline" onClick={() => toggleFeatured("service", p.id, p.featured_as_new)} title={p.featured_as_new ? "Retirer des Nouveautés" : "Épingler en Nouveautés"} data-testid={`toggle-featured-service-${p.id}`} className={p.featured_as_new ? "bg-[#FFF7ED] border-[#C85A32]" : ""}>
                    <Sparkles className={`w-4 h-4 ${p.featured_as_new ? "text-[#C85A32]" : ""}`}/>
                  </Button>
                )}
                <Button size="icon" variant="outline" onClick={() => openEdit("service", p)}><Edit3 className="w-4 h-4"/></Button>
                <Button size="icon" variant="ghost" onClick={() => del("service", p.id)}><Trash2 className="w-4 h-4 text-[#C85A32]"/></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="space-y-2" data-testid="received-orders">
            {orders.length === 0 && <p className="text-[#4B5563]">{t("sd.no_orders")}</p>}
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-[#1A3322]/10 rounded-lg p-4" data-testid={`order-${o.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold">#{o.id.slice(0, 8)} {o.reference && <span className="ml-2 text-xs font-mono bg-[#F5F5F0] px-2 py-0.5 rounded">{o.reference}</span>}</p>
                    <p className="text-xs text-[#4B5563]">{new Date(o.created_at).toLocaleString(locale)} · {o.provider || "pawapay"}</p>
                  </div>
                  <span className="text-sm px-3 py-1 rounded-full bg-[#F5F5F0] font-semibold">{o.status}</span>
                </div>
                <ul className="mt-3 text-sm space-y-1">
                  {o.items.map((it) => <li key={`${o.id}-${it.kind}-${it.item_id}`}>· {it.title} × {it.quantity} — {(it.price * it.quantity).toFixed(2)} €</li>)}
                </ul>
                {o.provider === "bank_transfer" && o.proof_url && o.payment_status !== "paid" && (
                  <div className="mt-3 p-3 bg-[#D98A2C]/10 border border-[#D98A2C] rounded" data-testid={`transfer-proof-${o.id}`}>
                    <p className="font-semibold text-[#C85A32] text-sm">{t("sd.proof_received")}</p>
                    <a href={o.proof_url} target="_blank" rel="noreferrer" className="text-[#1A3322] underline text-sm">{t("sd.view_proof")}</a>
                    {o.proof_reference && <p className="text-xs mt-1">Réf: <span className="font-mono">{o.proof_reference}</span></p>}
                    {o.proof_note && <p className="text-xs mt-1 italic">{o.proof_note}</p>}
                    <Button size="sm" onClick={() => confirmTransfer(o.id)} className="mt-2 pill-btn bg-[#1A3322] rounded-full" data-testid={`confirm-transfer-${o.id}`}>{t("sd.confirm_transfer")}</Button>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {["confirmed", "shipped", "delivered", "cancelled"].map((s) => (
                    <Button key={s} size="sm" variant="outline" onClick={() => updateOrderStatus(o.id, s)} data-testid={`set-status-${o.id}-${s}`}>{s}</Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="revenue" className="mt-6" data-testid="revenue-tab">
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-[#1A3322]/10 rounded-lg p-5" data-testid="revenue-held">
              <Clock className="w-6 h-6 text-amber-600 mb-2"/>
              <p className="text-xs uppercase tracking-wider text-[#4B5563]">{t("sd.pending_days_pre")} ({revenue.retention_days}j)</p>
              <p className="heading font-extrabold text-2xl mt-1">{revenue.held_eur.toFixed(2)} €</p>
            </div>
            <div className="bg-white border-2 border-[#C85A32] rounded-lg p-5" data-testid="revenue-releasable">
              <Wallet className="w-6 h-6 text-[#C85A32] mb-2"/>
              <p className="text-xs uppercase tracking-wider text-[#4B5563]">{t("sd.available")}</p>
              <p className="heading font-extrabold text-2xl mt-1 text-[#C85A32]">{revenue.releasable_eur.toFixed(2)} €</p>
            </div>
            <div className="bg-white border border-[#1A3322]/10 rounded-lg p-5" data-testid="revenue-paidout">
              <CheckCircle2 className="w-6 h-6 text-green-600 mb-2"/>
              <p className="text-xs uppercase tracking-wider text-[#4B5563]">{t("sd.paid_out")}</p>
              <p className="heading font-extrabold text-2xl mt-1">{revenue.paid_out_eur.toFixed(2)} €</p>
            </div>
          </div>
          <p className="text-xs text-[#4B5563] mb-4">{t("sd.commission_note")} <strong>{(revenue.commission_rate * 100).toFixed(1)}%</strong> · {t("sd.retention")} <strong>{revenue.retention_days} {t("sd.days_after")}</strong></p>
          <div className="space-y-2" data-testid="revenue-lines">
            {revenue.lines.length === 0 && (
              <p className="text-sm text-[#4B5563] py-8 text-center bg-white border border-dashed border-[#1A3322]/15 rounded-lg">{t("sd.no_paid_orders")}</p>
            )}
            {revenue.lines.map((l) => (
              <div key={l.order_id} className="bg-white border border-[#1A3322]/10 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`revenue-line-${l.order_id}`}>
                <div className="flex-1 min-w-[140px]">
                  <p className="font-mono text-sm font-semibold">{l.reference}</p>
                  <p className="text-[11px] text-[#4B5563]">{new Date(l.created_at).toLocaleDateString(locale)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#4B5563]">{t("sd.sale")}</p>
                  <p className="font-semibold">{l.subtotal_eur.toFixed(2)} €</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#4B5563]">{t("sd.commission")}</p>
                  <p className="text-[#C85A32]">-{l.commission_eur.toFixed(2)} €</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#4B5563]">{t("sd.net")}</p>
                  <p className="font-bold text-[#1A3322]">{l.amount_eur.toFixed(2)} €</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${l.payout_status === "paid_out" ? "bg-green-100 text-green-800" : l.releasable_now ? "bg-[#C85A32] text-white" : "bg-amber-100 text-amber-900"}`}>
                  {l.payout_status === "paid_out" ? t("sd.paid_out_badge") : l.releasable_now ? t("sd.to_release") : `${t("sd.blocked_until")} (${new Date(l.release_at).toLocaleDateString(locale)})`}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="shop" className="mt-6" data-testid="shop-tab">
          <div className="bg-white border border-[#1A3322]/10 rounded-lg overflow-hidden">
            {/* Cover preview */}
            <div className="relative h-40 bg-gradient-to-br from-[#1A3322] via-[#24472F] to-[#C85A32]">
              {shop.cover_image && (
                <img src={shop.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="shop-cover-preview"/>
              )}
              <div className="absolute bottom-3 left-5 flex items-end gap-3">
                <div className="w-16 h-16 rounded-xl bg-white border-2 border-white shadow-md flex items-center justify-center overflow-hidden" data-testid="shop-logo-preview">
                  {shop.logo_url
                    ? <img src={shop.logo_url} alt="" className="w-full h-full object-contain p-1"/>
                    : <Store className="w-8 h-8 text-[#1A3322]"/>
                  }
                </div>
                <p className="text-white heading font-extrabold text-xl pb-1 drop-shadow">{shop.name || t("sd.shop_placeholder")}</p>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("sd.shop_logo")}</Label>
                  <Input type="file" accept="image/*" onChange={uploadShopImage("logo_url")} data-testid="shop-logo-upload"/>
                  <p className="text-[11px] text-[#4B5563]">{t("sd.shop_logo_hint")}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t("sd.shop_cover")}</Label>
                  <Input type="file" accept="image/*" onChange={uploadShopImage("cover_image")} data-testid="shop-cover-upload"/>
                  <p className="text-[11px] text-[#4B5563]">{t("sd.shop_cover_hint")}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("sd.shop_name")}</Label>
                <Input value={shop.name} onChange={(e) => setShop({ ...shop, name: e.target.value })} data-testid="shop-name-input"/>
              </div>
              <div className="space-y-2">
                <Label>{t("sd.shop_desc")}</Label>
                <Textarea rows={3} value={shop.description} onChange={(e) => setShop({ ...shop, description: e.target.value })} data-testid="shop-desc-input" placeholder={t("sd.shop_desc_placeholder")}/>
              </div>
              <div className="space-y-2">
                <Label>{t("sd.shop_location")}</Label>
                <Input value={shop.location} onChange={(e) => setShop({ ...shop, location: e.target.value })} data-testid="shop-location-input" placeholder="Kinshasa, RDC"/>
              </div>

              <div className={`rounded-lg border p-3 flex items-start gap-3 ${shop.published ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`} data-testid="shop-published-block">
                <input
                  type="checkbox"
                  checked={shop.published}
                  onChange={(e) => setShop({ ...shop, published: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#1A3322]"
                  data-testid="shop-published-toggle"
                  id="shop-published-toggle-input"
                />
                <label htmlFor="shop-published-toggle-input" className="flex-1 cursor-pointer text-sm">
                  <p className={`font-semibold ${shop.published ? "text-green-800" : "text-amber-900"}`}>
                    {shop.published ? `🟢 ${t("sd.shop_published_on")}` : `⏸️ ${t("sd.shop_published_off")}`}
                  </p>
                  <p className="text-xs text-[#4B5563] mt-0.5">
                    {shop.published ? t("sd.shop_published_hint_on") : t("sd.shop_published_hint_off")}
                  </p>
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={saveShop} disabled={savingShop} className="pill-btn bg-[#1A3322] rounded-full" data-testid="shop-save-btn">
                  {savingShop ? t("common.loading") : t("sd.shop_save")}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* CSV Import Dialog */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle>{t("sd.csv_dialog_title")} — {csvKind === "product" ? t("sd.tab_products") : t("sd.tab_services")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#F5F5F0] border border-[#1A3322]/10 rounded-lg p-3 text-xs text-[#111812] space-y-1.5">
              <p className="font-semibold flex items-center gap-1.5"><FileText className="w-3.5 h-3.5"/>{t("sd.csv_format")}</p>
              <p className="font-mono text-[11px] leading-relaxed break-all">
                {csvKind === "product"
                  ? "title,description,price_eur,price_usd,price_cdf,stock,category_id,location,images"
                  : "title,description,price_eur,price_usd,price_cdf,category_id,zone,images"}
              </p>
              <p className="text-[#4B5563]">{t("sd.csv_hint")}</p>
              <p className="text-[#4B5563]">{t("sd.csv_images_hint")}</p>
            </div>
            <div className="space-y-2">
              <Label>{t("sd.csv_file")}</Label>
              <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} data-testid="csv-file-input"/>
            </div>
            {csvResult && (
              <div className="rounded-lg border p-3 text-sm" data-testid="csv-result">
                <p className="font-semibold text-[#1A3322]">✅ {csvResult.created} {t("sd.csv_created")} · ⚠️ {csvResult.skipped} {t("sd.csv_skipped")}</p>
                {csvResult.errors && csvResult.errors.length > 0 && (
                  <ul className="mt-2 space-y-0.5 text-xs text-[#C85A32] max-h-40 overflow-y-auto">
                    {csvResult.errors.slice(0, 20).map((e, i) => <li key={i}>Ligne {e.row}: {e.error}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={submitCsv} disabled={csvBusy || !csvFile} className="pill-btn bg-[#1A3322] rounded-full" data-testid="csv-submit-btn">
                <Upload className="w-4 h-4 mr-2"/>{csvBusy ? t("sd.csv_importing") : t("sd.csv_import_btn")}
              </Button>
              <Button variant="ghost" onClick={() => setCsvOpen(false)}>{t("if.cancel")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("if.edit") : t("if.new")} {dialogKind === "product" ? t("if.product") : t("if.service")}</DialogTitle></DialogHeader>
          <ItemForm t={t} kind={dialogKind} item={editing} categories={categories.filter((c) => c.kind === dialogKind || c.kind === "both")}
            onSave={() => { setDialogOpen(false); load(); }} onCancel={() => setDialogOpen(false)}/>
        </DialogContent>
      </Dialog>
    </div>
  );
}
